import React, { useCallback, useEffect, useMemo, useState } from "react";
import { africaApi } from "@/api/africaGlobalAiClient";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — AdminOne Africa Investment Watchtower™ (Phase 19)
 *
 * Strictly additive page. Surfaces the Africa Investment Watchtower™ for
 * AdminOne / paid members:
 *   - Market Pulse heatmap (11 countries + regions + sectors)
 *   - Country detail drilldown (Africa Oracle Score™ + signals + FX + sectors)
 *   - LOGIX-2 Africa Investment Daily Brief™
 *   - Personal watchlist (Pro+)
 */

function Pill({ children, color = "#7ac860", on = true, onClick }) {
  return (
    <button
      type="button"
      className="mesh-pill"
      onClick={onClick}
      style={{
        borderColor: color,
        color: on ? color : "var(--clx-muted)",
        background: on ? `${color}1a` : "transparent",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      {children}
    </button>
  );
}

function Bar({ value = 0, color = "#7ac860" }) {
  const v = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div style={{ height: 8, width: "100%", background: "rgba(255,255,255,.06)", borderRadius: 999, overflow: "hidden" }}>
      <div style={{ width: `${v}%`, height: "100%", background: color, borderRadius: 999 }} />
    </div>
  );
}

function scoreColor(score) {
  if (score >= 90) return "#c9a544";
  if (score >= 80) return "#7ac860";
  if (score >= 70) return "#60b8e8";
  if (score >= 60) return "#f5c842";
  return "#e87040";
}

export default function AdminAfricaWatchtower() {
  const [pulse, setPulse] = useState(null);
  const [brief, setBrief] = useState(null);
  const [signals, setSignals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("pulse"); // pulse | brief | signals | watchlist
  const [newSymbol, setNewSymbol] = useState("");
  const [newNote, setNewNote] = useState("");

  const loadCore = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const [{ data: p }, { data: b }, { data: s }, { data: w }] = await Promise.all([
        africaApi.pulse(false),
        africaApi.dailyBrief(),
        africaApi.signals(30),
        africaApi.watchlistGet(),
      ]);
      setPulse(p);
      setBrief(b);
      setSignals(s.signals || []);
      setWatchlist(w.items || []);
      if ((p?.countries || []).length && !selected) {
        setSelected((p.countries[0] || {}).code || "NG");
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load Africa watchtower");
    } finally {
      setBusy(false);
    }
  }, [selected]);

  useEffect(() => { loadCore(); }, [loadCore]);

  // Lazy-load country detail when selection changes
  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await africaApi.country(selected);
        if (!cancelled) setDetail(data);
      } catch (e) {
        if (!cancelled) setDetail(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selected]);

  const addWatch = useCallback(async () => {
    if (!newSymbol.trim()) return;
    setBusy(true);
    try {
      await africaApi.watchlistAdd({ symbol: newSymbol.trim().toUpperCase(), note: newNote.trim() || null });
      setNewSymbol(""); setNewNote("");
      const { data: w } = await africaApi.watchlistGet();
      setWatchlist(w.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to add");
    } finally {
      setBusy(false);
    }
  }, [newSymbol, newNote]);

  const removeWatch = useCallback(async (id) => {
    setBusy(true);
    try {
      await africaApi.watchlistDelete(id);
      const { data: w } = await africaApi.watchlistGet();
      setWatchlist(w.items || []);
    } finally { setBusy(false); }
  }, []);

  const tier = pulse?.tier || "professional";
  const isPreview = pulse?.preview === true;

  const sectorTop = useMemo(() => (pulse?.sectors || []).slice(0, 12), [pulse]);
  const regions = useMemo(() => pulse?.regions || [], [pulse]);

  return (
    <div className="admin-page" data-testid="admin-africa-watchtower">
      {/* Phase 26C — Mandated Compliance Lock™ banner */}
      <ComplianceLock variant="banner" testid="africa-watchtower-compliance" />
      <header className="admin-page__hd">
        <div>
          <h1 className="admin-page__title">Africa Investment Watchtower™</h1>
          <p className="admin-page__sub">LOGIX-2™ · Africa Oracle Score™ · 11 markets · 19 sectors · daily intelligence</p>
        </div>
        <div className="admin-page__badges" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <span className="admin-badge is-on">LIVE</span>
          <span className="admin-badge is-on">COMPLIANCE GUARD</span>
          <span className="admin-badge">Tier: {String(tier).toUpperCase()}</span>
          {isPreview && <span className="admin-badge is-warn">PREVIEW MODE</span>}
        </div>
      </header>

      {err && <div className="oracle-error" data-testid="admin-africa-error">{err}</div>}

      <div className="admin-tabs" role="tablist" style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "12px 0" }}>
        {[{ k: "pulse", label: "Market Pulse" }, { k: "brief", label: "Daily Brief" }, { k: "signals", label: "Signals" }, { k: "watchlist", label: "Watchlist" }].map((t) => (
          <button
            key={t.k}
            type="button"
            role="tab"
            aria-selected={tab === t.k}
            className={`oracle-btn ${tab === t.k ? "oracle-btn--gold" : "oracle-btn--ghost"}`}
            onClick={() => setTab(t.k)}
            data-testid={`admin-africa-tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
        <button type="button" className="oracle-btn oracle-btn--ghost" onClick={loadCore} disabled={busy} data-testid="admin-africa-refresh">
          {busy ? "…" : "↻ Refresh"}
        </button>
      </div>

      {tab === "pulse" && (
        <>
          {/* Region rollup */}
          <section className="admin-section" data-testid="admin-africa-regions">
            <h3 className="admin-section__title">Region Rollup</h3>
            <div className="admin-metric-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))" }}>
              {regions.map((r) => (
                <div className="admin-metric" key={r.region}>
                  <div className="admin-metric__label">{r.region}</div>
                  <div className="admin-metric__value" style={{ color: scoreColor(r.avg_score) }}>{r.avg_score}</div>
                  <div style={{ fontSize: 11, color: "var(--clx-muted)" }}>Top: {r.top?.name} · {r.top?.africa_oracle_score}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Country heatmap */}
          <section className="admin-section" data-testid="admin-africa-countries">
            <h3 className="admin-section__title">Country Heatmap · Africa Oracle Score™</h3>
            <div style={{ overflowX: "auto" }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Region</th>
                    <th>Score</th>
                    <th>Grade</th>
                    <th style={{ minWidth: 220 }}>Heatbar</th>
                    <th>Currency</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(pulse?.countries || []).map((c) => (
                    <tr key={c.code} data-testid={`admin-africa-row-${c.code}`}>
                      <td><strong>{c.name}</strong> <code style={{ color: "var(--clx-muted)" }}>{c.code}</code></td>
                      <td>{c.region}</td>
                      <td style={{ color: scoreColor(c.africa_oracle_score), fontWeight: 700 }}>{c.africa_oracle_score}</td>
                      <td><Pill color={scoreColor(c.africa_oracle_score)}>{c.grade}</Pill></td>
                      <td><Bar value={c.africa_oracle_score} color={scoreColor(c.africa_oracle_score)} /></td>
                      <td><code>{c.currency || "—"}</code></td>
                      <td>
                        <button
                          type="button"
                          className="oracle-btn oracle-btn--ghost oracle-btn--sm"
                          onClick={() => { setSelected(c.code); setTab("signals"); }}
                          data-testid={`admin-africa-drill-${c.code}`}
                        >
                          Drill ▸
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Sector heatmap */}
          <section className="admin-section" data-testid="admin-africa-sectors">
            <h3 className="admin-section__title">Top Sector Opportunities</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 10 }}>
              {sectorTop.map((s) => (
                <div key={s.sector} className="admin-card" style={{ padding: 12, border: "1px solid var(--clx-border)", borderRadius: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                    <strong style={{ fontSize: 13 }}>{s.sector}</strong>
                    <span style={{ color: scoreColor(s.score), fontWeight: 700 }}>{s.score}</span>
                  </div>
                  <Bar value={s.score} color={scoreColor(s.score)} />
                  <div style={{ fontSize: 10, color: "var(--clx-muted)", marginTop: 4 }}>{s.grade}</div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {tab === "brief" && (
        <section className="admin-section" data-testid="admin-africa-brief">
          <h3 className="admin-section__title">{brief?.title || "LOGIX-2 Africa Investment Intelligence Brief™"}</h3>
          <p className="admin-foot-note">Generated: {brief?.generated_at}</p>
          {(brief?.sections || []).map((sec) => (
            <div key={sec.id} className="admin-card" style={{ marginTop: 12, padding: 14, border: "1px solid var(--clx-border)", borderRadius: 10 }} data-testid={`admin-africa-brief-section-${sec.id}`}>
              <h4 style={{ margin: 0, marginBottom: 6, color: "var(--clx-gold)", fontFamily: "'Cinzel',serif", letterSpacing: ".06em" }}>{sec.title}</h4>
              {sec.content && <p style={{ margin: 0, color: "var(--clx-text-bright,#fff7d6)" }}>{sec.content}</p>}
              {Array.isArray(sec.rows) && (
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {sec.rows.map((r, i) => (
                    <li key={i} style={{ fontSize: 13, lineHeight: 1.6 }}>
                      {r.name && <strong>{r.name}</strong>}{r.code ? ` (${r.code})` : ""}
                      {r.currency ? ` · ${r.currency}` : ""}
                      {r.risk_band ? ` · ${r.risk_band}` : ""}
                      {r.currency_risk != null ? ` · FX risk ${Math.round(r.currency_risk)}` : ""}
                      {r.compliance_risk != null ? ` · compliance ${Math.round(r.compliance_risk)}` : ""}
                      {r.sector ? <span><strong>{r.sector}</strong> · score {r.score}</span> : ""}
                    </li>
                  ))}
                </ul>
              )}
              {Array.isArray(sec.signals) && (
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {sec.signals.map((s) => (
                    <li key={s.id} style={{ fontSize: 13, lineHeight: 1.6, color: s.color || "inherit" }}>
                      <strong>{s.label}</strong> · {s.text}
                    </li>
                  ))}
                </ul>
              )}
              {Array.isArray(sec.adds) && (
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {sec.adds.map((a, i) => (
                    <li key={i} style={{ fontSize: 13 }}><code>{a.symbol}</code> · {a.name || a.region} · {a.reason}</li>
                  ))}
                </ul>
              )}
              {Array.isArray(sec.items) && (
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {sec.items.length === 0 ? <li style={{ color: "var(--clx-muted)" }}>None flagged for today.</li> : sec.items.map((it, i) => <li key={i} style={{ fontSize: 13 }}>{it}</li>)}
                </ul>
              )}
            </div>
          ))}
          {brief?.disclaimer && <p className="admin-foot-note" style={{ marginTop: 12 }}>{brief.disclaimer}</p>}
        </section>
      )}

      {tab === "signals" && (
        <>
          {/* Country selector */}
          <section className="admin-section" data-testid="admin-africa-country-picker">
            <h3 className="admin-section__title">Country Detail</h3>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {(pulse?.countries || []).map((c) => (
                <Pill key={c.code} color={scoreColor(c.africa_oracle_score)} on={selected === c.code} onClick={() => setSelected(c.code)}>
                  {c.name} · {c.africa_oracle_score}
                </Pill>
              ))}
            </div>
            {detail && (
              <div style={{ marginTop: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10 }}>
                  <div className="admin-metric">
                    <div className="admin-metric__label">Africa Oracle Score™</div>
                    <div className="admin-metric__value" style={{ color: scoreColor(detail.score.africa_oracle_score) }}>{detail.score.africa_oracle_score}</div>
                    <div style={{ fontSize: 11, color: "var(--clx-muted)" }}>{detail.score.grade}</div>
                  </div>
                  <div className="admin-metric">
                    <div className="admin-metric__label">FX Currency</div>
                    <div className="admin-metric__value" style={{ fontSize: 18 }}>{detail.fx.currency} · {detail.fx.trend}</div>
                    <div style={{ fontSize: 11, color: "var(--clx-muted)" }}>Vol-30d {detail.fx.vol_30d}% · risk {detail.fx.risk_band}</div>
                  </div>
                  <div className="admin-metric">
                    <div className="admin-metric__label">Top Sector</div>
                    <div className="admin-metric__value" style={{ fontSize: 16 }}>{detail.sectors?.[0]?.sector || "—"}</div>
                    <div style={{ fontSize: 11, color: "var(--clx-muted)" }}>Score {detail.sectors?.[0]?.score} · {detail.sectors?.[0]?.grade}</div>
                  </div>
                </div>

                <h4 style={{ marginTop: 14, color: "var(--clx-gold)" }}>Signals · {detail.country.name}</h4>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 10 }}>
                  {(detail.signals || []).map((s) => (
                    <div key={s.id} className="admin-card" style={{ padding: 12, border: `1px solid ${s.color}66`, borderRadius: 10, background: `${s.color}11` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <strong style={{ color: s.color }}>{s.icon} {s.label}</strong>
                        <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 11 }}>{s.score}</span>
                      </div>
                      <p style={{ margin: "6px 0 0", fontSize: 13 }}>{s.text}</p>
                      <div style={{ fontSize: 10, color: "var(--clx-muted)", marginTop: 4 }}>{s.severity} · {s.tone}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Combined signals feed */}
          <section className="admin-section" data-testid="admin-africa-signals-feed">
            <h3 className="admin-section__title">Active Signals · All Africa</h3>
            <div style={{ overflowX: "auto" }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Scope</th>
                    <th>Severity</th>
                    <th>Score</th>
                    <th>Text</th>
                  </tr>
                </thead>
                <tbody>
                  {(signals || []).map((s) => (
                    <tr key={s.id} data-testid={`admin-africa-signal-${s.type}`}>
                      <td><strong>{s.label}</strong></td>
                      <td><code>{s.scope}</code></td>
                      <td><Pill color={s.color}>{s.severity}</Pill></td>
                      <td style={{ color: scoreColor(s.score), fontWeight: 700 }}>{s.score}</td>
                      <td>{s.text}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {tab === "watchlist" && (
        <section className="admin-section" data-testid="admin-africa-watchlist">
          <h3 className="admin-section__title">My Africa Watchlist</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            <input className="oracle-input" placeholder="Symbol or country code (e.g. NG)" value={newSymbol} onChange={(e) => setNewSymbol(e.target.value)} data-testid="admin-africa-watch-symbol" />
            <input className="oracle-input" placeholder="Note (optional)" value={newNote} onChange={(e) => setNewNote(e.target.value)} style={{ minWidth: 240 }} data-testid="admin-africa-watch-note" />
            <button type="button" className="oracle-btn oracle-btn--gold" onClick={addWatch} disabled={busy || !newSymbol.trim()} data-testid="admin-africa-watch-add">+ Add</button>
          </div>
          {watchlist.length === 0 ? (
            <div className="oracle-empty" style={{ padding: 14 }}>Watchlist empty. Add a country code, ETF, or thesis symbol.</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr><th>Symbol</th><th>Note</th><th>Added</th><th>Action</th></tr>
              </thead>
              <tbody>
                {watchlist.map((w) => (
                  <tr key={w.id} data-testid={`admin-africa-watch-row-${w.id}`}>
                    <td><code>{w.symbol}</code></td>
                    <td>{w.note || "—"}</td>
                    <td style={{ fontFamily: "'Space Mono',monospace", fontSize: 11 }}>{(w.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td><button type="button" className="oracle-btn oracle-btn--ghost oracle-btn--sm mesh-btn--danger" onClick={() => removeWatch(w.id)} data-testid={`admin-africa-watch-remove-${w.id}`}>Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      <p className="admin-foot-note" style={{ marginTop: 16 }}>
        <em>{pulse?.disclaimer || "Research and education only. No guaranteed outcomes."}</em>
      </p>
    </div>
  );
}
